/**
 * PushToEmrCard Component
 * Displays push to EMR functionality and filling history
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  Upload,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  RotateCcw,
  Info,
} from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  pushToEmr,
  getPushCodingStatus,
  getFillingHistory,
  recalculatePaymentInfo,
  type FillingHistoryItem,
} from '../services/rogerDocApi';
import { createState } from '../services/diagnosisStateService';
import CardTitleWithIcon from './CardTitleWithIcon';
import type {
  SavedDiagnosisState,
  DiagnosisStateData,
} from '../types/diagnosisState.types';
import { getStateNumber } from '../utils/diagnosisStateUtils';

const ERROR_MESSAGES: Record<string, string> = {
  AlreadySubmittedError:
    'Filling failed because the visit can not be edited. Move the visit into "Submitted to Case Manager" state and try again.',
  NoVisitFoundError:
    'Visit could not be found in the EMR. If you believe this is an error, please contact Roger support.',
  NoPatientFoundError:
    'Visit could not be found in the EMR. If you believe this is an error, please contact Roger support.',
  DEFAULT:
    'An unknown kinnser error occurred. Please try again. If the problem persists, please contact Roger support.',
};

const ActionRequiredTooltip: React.FC<{ message: string; title?: string }> = ({
  message,
  title = 'Action Required',
}) => {
  const [open, setOpen] = useState(false);

  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <div
            className='flex items-center gap-1.5 text-red-600 cursor-help'
            onClick={() => setOpen(prev => !prev)}
          >
            <Info className='w-4 h-4' />
            <span className='text-sm font-medium underline decoration-dotted underline-offset-4'>
              {title}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent className='max-w-[300px] p-3'>
          <p className='text-sm leading-relaxed whitespace-pre-line'>
            {message}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

interface PushToEmrCardProps {
  diagnosisId: string;
  manualSaveStateId?: string;
  savedStates?: SavedDiagnosisState[];
  onStateCreated?: () => void;
  currentState?: DiagnosisStateData;
  hasUnsavedChanges?: boolean;
}

const PushToEmrCard: React.FC<PushToEmrCardProps> = ({
  diagnosisId,
  manualSaveStateId,
  savedStates = [],
  onStateCreated,
  currentState,
  hasUnsavedChanges = false,
}) => {
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );
  const [isPushingToEmr, setIsPushingToEmr] = useState(false);
  const [fillingHistory, setFillingHistory] = useState<FillingHistoryItem[]>(
    []
  );
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] =
    useState<FillingHistoryItem | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [revertingItemId, setRevertingItemId] = useState<string | null>(null);
  const [revertingToFilled, setRevertingToFilled] = useState(false);
  const [pushConfirmOpen, setPushConfirmOpen] = useState(false);
  const [revertConfirmOpen, setRevertConfirmOpen] = useState(false);
  const [pendingRevertItem, setPendingRevertItem] =
    useState<FillingHistoryItem | null>(null);
  const [pendingRevertUsePrevious, setPendingRevertUsePrevious] =
    useState(false);

  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Determine the state to be pushed
  const stateToPush = React.useMemo(() => {
    if (!manualSaveStateId) {
      return savedStates.find(s => s.id === 'original' || s.isOriginal);
    }
    return savedStates.find(s => s.id === manualSaveStateId);
  }, [manualSaveStateId, savedStates]);

  // Validation logic
  const validationErrors = React.useMemo(() => {
    const errors: string[] = [];

    // Use currentState for validation if provided (WYSIWYG), otherwise fallback to saved state
    const dataToValidate = currentState || stateToPush;

    if (!dataToValidate) {
      // If no state is found/loaded yet, we might want to wait or consider it an error
      // Assuming if savedStates is empty, we can't push.
      if (savedStates.length > 0) {
        errors.push('No diagnosis state found to push.');
      }
      return errors;
    }

    const hasPrimary = !!(
      dataToValidate.primary_diagnosis &&
      dataToValidate.primary_diagnosis.diagnosis &&
      dataToValidate.primary_diagnosis.diagnosis.name
    );
    const hasSecondary =
      (dataToValidate.secondary_diagnoses?.diagnoses?.length || 0) > 0;

    if (!hasPrimary) {
      errors.push('No primary diagnosis available to push.');
    }
    if (!hasSecondary) {
      errors.push(
        'No secondary diagnoses available to push. Add at least one secondary diagnosis.'
      );
    }

    // Check for unsaved changes using the shared state passed from parent
    if (hasUnsavedChanges) {
      errors.push('Save your unsaved changes before pushing.');
    }

    return errors;
  }, [stateToPush, currentState, savedStates.length, hasUnsavedChanges]);

  const isPushDisabled =
    isPushingToEmr || !!revertingItemId || validationErrors.length > 0;

  // Load filling history on mount and when diagnosisId changes
  useEffect(() => {
    loadFillingHistory(true);
  }, [diagnosisId]);

  const loadFillingHistory = async (isInitial: boolean = false) => {
    try {
      // Only show loading on initial load
      if (isInitial) {
        setLoadingHistory(true);
      }
      const history = await getFillingHistory(diagnosisId);

      // Merge new history with existing history
      setFillingHistory(prevHistory => {
        // Create a map of existing history by ID
        const existingMap = new Map(prevHistory.map(item => [item.id, item]));
        const mergedHistory: FillingHistoryItem[] = [];

        // Process new history - add new entries and update existing ones
        for (const newItem of history) {
          const existingItem = existingMap.get(newItem.id);
          if (existingItem) {
            // Update existing item if status or end_time changed
            if (
              existingItem.status !== newItem.status ||
              existingItem.end_time !== newItem.end_time
            ) {
              mergedHistory.push(newItem);
            } else {
              mergedHistory.push(existingItem);
            }
            existingMap.delete(newItem.id);
          } else {
            // New item - add it
            mergedHistory.push(newItem);
          }
        }

        // Sort by start_time descending (most recent first)
        mergedHistory.sort((a, b) => {
          const timeA = new Date(a.start_time).getTime();
          const timeB = new Date(b.start_time).getTime();
          return timeB - timeA;
        });

        return mergedHistory;
      });
    } catch (error) {
      console.error('Error loading filling history:', error);
      // Don't show error toast for history loading failures
    } finally {
      if (isInitial) {
        setLoadingHistory(false);
      }
    }
  };

  const handlePushToEmr = () => {
    setPushConfirmOpen(true);
  };

  const handleConfirmPushToEmr = async () => {
    setPushConfirmOpen(false);
    // Clear any existing polling
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }

    try {
      setIsPushingToEmr(true);
      const user = useStore.getState().user;
      await pushToEmr('wellsky', diagnosisId, manualSaveStateId, user?.email);

      // Refresh history immediately to show the new "Running" item
      loadFillingHistory();

      // Start polling for status
      const pollInterval = 8000; // Poll every 8 seconds
      const maxPollAttempts = 40; // Max 4 minutes (40 * 8s)
      let pollAttempts = 0;

      const pollStatus = async (): Promise<void> => {
        if (pollAttempts >= maxPollAttempts) {
          setIsPushingToEmr(false);
          pollTimeoutRef.current = null;
          showError('Push to EMR timed out. Please check the status manually.');
          await loadFillingHistory(); // Refresh history
          return;
        }

        try {
          const status = await getPushCodingStatus(
            diagnosisId,
            manualSaveStateId
          );

          if (status.status === 'RUNNING') {
            // Continue polling
            pollAttempts++;
            // Refresh history to ensure running state is visible if it wasn't caught immediately
            await loadFillingHistory();
            pollTimeoutRef.current = setTimeout(pollStatus, pollInterval);
          } else {
            // Stop polling
            setIsPushingToEmr(false);
            pollTimeoutRef.current = null;

            // Refresh history to show the new entry
            await loadFillingHistory();

            // Check for failure states
            if (
              status.status === 'FAILED' ||
              status.status === 'CANCELED' ||
              status.status === 'TERMINATED' ||
              status.status === 'TIMED_OUT'
            ) {
              const errorMessage = 'Push to EMR failed.';
              showError(errorMessage);
            } else if (status.status === 'COMPLETED') {
              // Show success toast
              showSuccess('Successfully pushed to EMR');
            } else {
              // Other non-running states
              showSuccess('Successfully pushed to EMR');
            }
          }
        } catch (error) {
          console.error('Error polling push status:', error);
          setIsPushingToEmr(false);
          pollTimeoutRef.current = null;
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to check push status';
          showError(errorMessage);
          await loadFillingHistory(); // Refresh history even on error
        }
      };

      // Start polling after a short delay
      pollTimeoutRef.current = setTimeout(pollStatus, pollInterval);
    } catch (error) {
      console.error('Error pushing to EMR:', error);
      setIsPushingToEmr(false);
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to push to EMR';
      showError(errorMessage);
      await loadFillingHistory(); // Refresh history on error
    }
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    // Date strings come in as UTC but without timezone indicator (e.g., "2025-12-16T03:45:24.135000")
    // Append 'Z' to explicitly mark as UTC so it's parsed correctly
    const utcDateString =
      dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString)
        ? dateString
        : `${dateString}Z`;
    const date = new Date(utcDateString);
    // Use Intl.DateTimeFormat to convert UTC to viewer's timezone and locale
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(date);
  };

  const getStatusBadge = (status: string) => {
    switch (status.toUpperCase()) {
      case 'COMPLETED':
        return (
          <Badge variant='outline' className='text-green-700 border-green-400'>
            <CheckCircle className='w-3 h-3 mr-1' />
            Completed
          </Badge>
        );
      case 'FAILED':
      case 'CANCELED':
      case 'TERMINATED':
      case 'TIMED_OUT':
        return (
          <Badge variant='outline' className='text-red-700 border-red-400'>
            <XCircle className='w-3 h-3 mr-1' />
            {status}
          </Badge>
        );
      case 'RUNNING':
        return (
          <Badge variant='outline' className='text-blue-700 border-blue-400'>
            <Loader2 className='w-3 h-3 mr-1 animate-spin' />
            Running
          </Badge>
        );
      default:
        return (
          <Badge variant='outline' className='text-gray-700 border-gray-400'>
            <Clock className='w-3 h-3 mr-1' />
            {status}
          </Badge>
        );
    }
  };

  const getStateName = (codingStateId: string | null) => {
    if (!codingStateId) {
      const originalState = savedStates.find(
        s => s.id === 'original' || s.isOriginal
      );
      return originalState?.name || 'Original AI codes by Roger';
    }
    const state = savedStates.find(s => s.id === codingStateId);
    return state ? state.name : codingStateId;
  };

  const getErrorMessageComponent = (errorType?: string | null) => {
    if (!errorType) {
      // No error type, show nothing but a dash
      return <span className='text-gray-400 text-sm'>-</span>;
    }

    switch (errorType) {
      case 'AlreadySubmittedError':
        return <ActionRequiredTooltip message={ERROR_MESSAGES[errorType]} />;
      case 'NoVisitFoundError':
      case 'NoPatientFoundError':
        return (
          <ActionRequiredTooltip
            message={ERROR_MESSAGES[errorType]}
            title='Visit Not Found'
          />
        );
      default:
        return (
          <ActionRequiredTooltip
            message={ERROR_MESSAGES.DEFAULT}
            title='Unknown Kinnser Error'
          />
        );
    }
  };

  const handleOpenDetails = (item: FillingHistoryItem) => {
    setSelectedHistoryItem(item);
    setDetailModalOpen(true);
  };

  const handleRevertCodes = (
    item: FillingHistoryItem,
    usePreviousCodes: boolean
  ) => {
    const primaryDiagnosis = usePreviousCodes
      ? item.previous_primary_diagnosis
      : item.primary_diagnosis;
    const secondaryDiagnoses = usePreviousCodes
      ? item.previous_secondary_diagnoses
      : item.secondary_diagnoses;

    if (!primaryDiagnosis && !secondaryDiagnoses) {
      showError(
        `Cannot revert: No ${
          usePreviousCodes ? 'previous' : 'filled'
        } diagnosis codes found in this history item.`
      );
      return;
    }

    setPendingRevertItem(item);
    setPendingRevertUsePrevious(usePreviousCodes);
    setRevertConfirmOpen(true);
  };

  const handleConfirmRevertCodes = async () => {
    if (!pendingRevertItem) return;

    const item = pendingRevertItem;
    const usePreviousCodes = pendingRevertUsePrevious;
    setRevertConfirmOpen(false);
    setPendingRevertItem(null);

    const primaryDiagnosis = usePreviousCodes
      ? item.previous_primary_diagnosis
      : item.primary_diagnosis;
    const secondaryDiagnoses = usePreviousCodes
      ? item.previous_secondary_diagnoses
      : item.secondary_diagnoses;

    // Clear any existing polling
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }

    try {
      setRevertingItemId(item.id);
      setRevertingToFilled(!usePreviousCodes);

      // Generate a descriptive name for the reverted state
      const stateNumber = getStateNumber(item.coding_state_id, savedStates);
      // const stateName = getStateName(item.coding_state_id);
      const revertStateName = usePreviousCodes
        ? stateNumber
          ? `Reverted to EMR State ${stateNumber}`
          : 'Reverted to a Previous EMR State'
        : stateNumber
          ? `Reverted to Roger State ${stateNumber}`
          : 'Reverted to a Previous Roger State';

      // Recalculate case_mix_data from the codes
      const primaryDiagnosisName = primaryDiagnosis?.diagnosis.name || '';
      const secondaryDiagnosesNames =
        secondaryDiagnoses?.diagnoses.map(dx => dx.diagnosis.name) || [];

      const caseMixData = await recalculatePaymentInfo(
        diagnosisId,
        primaryDiagnosisName,
        secondaryDiagnosesNames
      );

      // Create new diagnosis state from the selected codes
      const newState = await createState(diagnosisId, revertStateName, {
        primary_diagnosis: primaryDiagnosis,
        secondary_diagnoses: secondaryDiagnoses,
        case_mix_data: caseMixData,
      });

      // Validate that the state was created successfully
      if (!newState || !newState.id) {
        throw new Error(
          'Failed to create diagnosis state: Invalid response from server'
        );
      }

      // Notify parent to refresh states so the new state appears in the list
      if (onStateCreated) {
        onStateCreated();
      }

      // Push the new state to EMR
      const user = useStore.getState().user;
      await pushToEmr('wellsky', diagnosisId, newState.id, user?.email);

      // Refresh history to show the new push
      await loadFillingHistory();

      // Start polling for the new push status
      const pollInterval = 8000;
      const maxPollAttempts = 40;
      let pollAttempts = 0;

      const pollStatus = async (): Promise<void> => {
        if (pollAttempts >= maxPollAttempts) {
          setRevertingItemId(null);
          pollTimeoutRef.current = null;
          showError('Revert push timed out. Please check the status manually.');
          await loadFillingHistory();
          return;
        }

        try {
          const status = await getPushCodingStatus(diagnosisId, newState.id);

          if (status.status === 'RUNNING') {
            pollAttempts++;
            // Refresh history to ensure running state is visible
            await loadFillingHistory();
            pollTimeoutRef.current = setTimeout(pollStatus, pollInterval);
          } else {
            setRevertingItemId(null);
            pollTimeoutRef.current = null;
            await loadFillingHistory();

            if (
              status.status === 'FAILED' ||
              status.status === 'CANCELED' ||
              status.status === 'TERMINATED' ||
              status.status === 'TIMED_OUT'
            ) {
              showError('Revert push to EMR failed.');
            } else if (status.status === 'COMPLETED') {
              showSuccess('Successfully reverted codes to EMR');
            } else {
              showSuccess('Revert push initiated successfully');
            }
          }
        } catch (error) {
          console.error('Error polling revert push status:', error);
          setRevertingItemId(null);
          pollTimeoutRef.current = null;
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to check revert push status';
          showError(errorMessage);
          await loadFillingHistory();
        }
      };

      pollTimeoutRef.current = setTimeout(pollStatus, pollInterval);
      showSuccess('Revert initiated. Creating state and pushing to EMR...');
    } catch (error) {
      console.error('Error reverting codes:', error);
      setRevertingItemId(null);
      setRevertingToFilled(false);
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to revert codes to previous state';
      showError(errorMessage);
    }
  };

  const renderDiagnosisCodes = (
    primaryDiagnosis?: FillingHistoryItem['primary_diagnosis'],
    secondaryDiagnoses?: FillingHistoryItem['secondary_diagnoses']
  ) => {
    return (
      <div className='space-y-4'>
        {/* Primary Diagnosis */}
        <div className='bg-blue-50 border border-blue-100 rounded-md p-3'>
          <div className='flex items-center justify-between mb-1'>
            <h4 className='text-sm font-bold text-blue-900'>
              Primary Diagnosis
            </h4>
          </div>
          {primaryDiagnosis ? (
            <div>
              <div className='text-sm font-medium text-blue-900'>
                {primaryDiagnosis.diagnosis.name}
              </div>
              <div className='text-xs text-blue-800'>
                {primaryDiagnosis.diagnosis.desc}
              </div>
            </div>
          ) : (
            <div className='text-xs text-gray-500 italic'>
              No primary diagnosis
            </div>
          )}
        </div>

        {/* Secondary Diagnoses */}
        <div>
          <h4 className='text-sm font-bold text-gray-800 mb-2'>
            Secondary Diagnoses
          </h4>
          {secondaryDiagnoses?.diagnoses &&
          secondaryDiagnoses.diagnoses.length > 0 ? (
            <div className='space-y-2'>
              {secondaryDiagnoses.diagnoses.map((dx, idx) => (
                <div
                  key={idx}
                  className='border rounded-md p-3 text-sm flex gap-3'
                >
                  <span className='font-bold text-gray-400 select-none'>
                    {idx + 1}.
                  </span>
                  <div className='flex items-baseline gap-2'>
                    <span className='font-mono font-bold text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded text-xs'>
                      {dx.diagnosis.name}
                    </span>
                    <span className='text-gray-800 font-medium'>
                      {dx.diagnosis.desc}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='text-sm text-gray-500 italic border rounded-md p-3'>
              No secondary diagnoses
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <CardTitleWithIcon
              icon={Upload}
              title='Push to EMR'
              subtitle='Send selected saved state to EMR'
            />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    tabIndex={0}
                    className={`inline-block ${
                      validationErrors.length > 0 ? 'cursor-not-allowed' : ''
                    }`}
                  >
                    <Button
                      onClick={handlePushToEmr}
                      variant='default'
                      disabled={isPushDisabled}
                      className={
                        validationErrors.length > 0
                          ? 'opacity-50 pointer-events-none'
                          : ''
                      }
                    >
                      {isPushingToEmr ? (
                        <>
                          <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                          Pushing...
                        </>
                      ) : (
                        <>
                          <Upload className='w-4 h-4 mr-2' />
                          Push to EMR
                        </>
                      )}
                    </Button>
                  </span>
                </TooltipTrigger>
                {validationErrors.length > 0 && (
                  <TooltipContent side='bottom' className='max-w-[300px] p-3'>
                    <div className='flex flex-col gap-1'>
                      <div className='font-semibold text-red-600 mb-1 flex items-center gap-2'>
                        <XCircle className='w-4 h-4' />
                        Cannot Push to EMR
                      </div>
                      {validationErrors.map((err, i) => (
                        <p key={i} className='text-sm text-gray-700'>
                          • {err}
                        </p>
                      ))}
                    </div>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardHeader>
        <CardContent>
          <div className='space-y-4'>
            <div>
              <h3 className='text-sm font-semibold text-gray-700 mb-3'>
                Filling History: States Pushed by Roger
              </h3>
              {loadingHistory ? (
                <div className='flex items-center justify-center py-8'>
                  <Loader2 className='w-6 h-6 animate-spin text-gray-400' />
                </div>
              ) : fillingHistory.length === 0 ? (
                <div className='text-sm text-gray-500 text-center py-8'>
                  No filling history available
                </div>
              ) : (
                <div className='border rounded-lg overflow-hidden'>
                  <div className='max-h-[320px] overflow-y-auto'>
                    <Table>
                      <TableHeader className='sticky top-0 bg-white z-10'>
                        <TableRow>
                          <TableHead className='w-[120px]'>Status</TableHead>
                          <TableHead>Pushed State</TableHead>
                          <TableHead>Message</TableHead>
                          <TableHead>Start Time</TableHead>
                          <TableHead>End Time</TableHead>
                          <TableHead>Duration</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {fillingHistory.map(item => {
                          const startTime = new Date(item.start_time);
                          const endTime = item.end_time
                            ? new Date(item.end_time)
                            : null;
                          const duration = endTime
                            ? Math.round(
                                (endTime.getTime() - startTime.getTime()) / 1000
                              )
                            : null;

                          return (
                            <TableRow key={item.id}>
                              <TableCell>
                                {getStatusBadge(item.status)}
                              </TableCell>
                              <TableCell>
                                <button
                                  className='text-blue-600 hover:underline hover:text-blue-800 text-left text-sm font-medium flex items-center gap-1.5 group'
                                  onClick={() => handleOpenDetails(item)}
                                >
                                  {(() => {
                                    const stateNumber = getStateNumber(
                                      item.coding_state_id,
                                      savedStates
                                    );
                                    const stateName = getStateName(
                                      item.coding_state_id
                                    );
                                    return stateNumber
                                      ? `State ${stateNumber} - ${stateName}`
                                      : stateName;
                                  })()}
                                  <Eye className='w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity' />
                                </button>
                              </TableCell>
                              <TableCell>
                                {getErrorMessageComponent(item.error_type)}
                              </TableCell>
                              <TableCell className='text-sm'>
                                {formatDateTime(item.start_time)}
                              </TableCell>
                              <TableCell className='text-sm'>
                                {item.end_time
                                  ? formatDateTime(item.end_time)
                                  : 'N/A'}
                              </TableCell>
                              <TableCell className='text-sm'>
                                {duration !== null
                                  ? `${duration}s`
                                  : 'In Progress'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className='max-w-6xl max-h-[80vh] flex flex-col overflow-hidden'>
          <DialogHeader>
            <DialogTitle>
              {selectedHistoryItem
                ? (() => {
                    const stateNumber = getStateNumber(
                      selectedHistoryItem.coding_state_id,
                      savedStates
                    );
                    return stateNumber
                      ? `Diagnosis Codes - State ${stateNumber}`
                      : `Diagnosis Codes - ${getStateName(
                          selectedHistoryItem.coding_state_id
                        )}`;
                  })()
                : 'Diagnosis Details'}
            </DialogTitle>
          </DialogHeader>
          <div className='flex-1 overflow-y-auto p-1'>
            {selectedHistoryItem && (
              <div className='grid grid-cols-2 gap-6'>
                {/* Left side - Filled Codes */}
                <div className='space-y-4 border rounded-lg p-4'>
                  <div className='flex items-center justify-between'>
                    <Badge
                      variant='outline'
                      className='text-green-700 border-green-400 bg-green-50 text-base font-semibold px-3 py-1'
                    >
                      State Filled by Roger
                    </Badge>
                    <Button
                      onClick={() =>
                        handleRevertCodes(selectedHistoryItem, false)
                      }
                      disabled={
                        revertingItemId === selectedHistoryItem.id &&
                        revertingToFilled
                      }
                      variant='default'
                      size='sm'
                    >
                      {revertingItemId === selectedHistoryItem.id &&
                      revertingToFilled ? (
                        <>
                          <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                          Reverting...
                        </>
                      ) : (
                        <>
                          <RotateCcw className='w-4 h-4 mr-2' />
                          Revert to this state
                        </>
                      )}
                    </Button>
                  </div>
                  {renderDiagnosisCodes(
                    selectedHistoryItem.primary_diagnosis,
                    selectedHistoryItem.secondary_diagnoses
                  )}
                </div>

                {/* Right side - Previous Codes */}
                <div className='space-y-4 border rounded-lg p-4'>
                  <div className='flex items-center justify-between'>
                    <Badge
                      variant='outline'
                      className='text-orange-700 border-orange-400 bg-orange-50 text-base font-semibold px-3 py-1'
                    >
                      Previous State in EMR
                    </Badge>
                    <Button
                      onClick={() =>
                        handleRevertCodes(selectedHistoryItem, true)
                      }
                      disabled={
                        revertingItemId === selectedHistoryItem.id &&
                        !revertingToFilled
                      }
                      variant='default'
                      size='sm'
                    >
                      {revertingItemId === selectedHistoryItem.id &&
                      !revertingToFilled ? (
                        <>
                          <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                          Reverting...
                        </>
                      ) : (
                        <>
                          <RotateCcw className='w-4 h-4 mr-2' />
                          Revert to this state
                        </>
                      )}
                    </Button>
                  </div>
                  {renderDiagnosisCodes(
                    selectedHistoryItem.previous_primary_diagnosis,
                    selectedHistoryItem.previous_secondary_diagnoses
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Push to EMR Confirmation Dialog */}
      <Dialog open={pushConfirmOpen} onOpenChange={setPushConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Push to EMR</DialogTitle>
            <DialogDescription>
              Are you sure you want to push the selected state to EMR? This will
              send the diagnosis codes to Wellsky.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setPushConfirmOpen(false)}
              disabled={isPushingToEmr}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmPushToEmr} disabled={isPushingToEmr}>
              {isPushingToEmr ? (
                <>
                  <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  Pushing...
                </>
              ) : (
                <>
                  <Upload className='w-4 h-4 mr-2' />
                  Push to EMR
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revert Codes Confirmation Dialog */}
      <Dialog open={revertConfirmOpen} onOpenChange={setRevertConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revert Codes</DialogTitle>
            <DialogDescription>
              Are you sure you want to revert to the{' '}
              {pendingRevertUsePrevious ? 'previous' : 'filled'} codes? This
              will create a new state and push it to EMR.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setRevertConfirmOpen(false);
                setPendingRevertItem(null);
              }}
              disabled={
                pendingRevertItem !== null &&
                revertingItemId === pendingRevertItem.id
              }
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRevertCodes}
              disabled={
                pendingRevertItem !== null &&
                revertingItemId === pendingRevertItem.id
              }
            >
              {pendingRevertItem !== null &&
              revertingItemId === pendingRevertItem.id ? (
                <>
                  <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  Reverting...
                </>
              ) : (
                <>
                  <RotateCcw className='w-4 h-4 mr-2' />
                  Revert
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PushToEmrCard;
